import React, { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Card,
    CardContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    CircularProgress,
    TableSortLabel,
    TextField,
    InputAdornment,
} from '@mui/material';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SearchIcon from '@material-ui/icons/Search';
import RestoreIcon from '@material-ui/icons/Restore';
import Dialog from '../../../../widgets/dialog';
import { useUploadReport, useGetReports, useDeleteReport, useDownloadReport, useRevertReportDeletion } from './hooks';
import theme from 'theme';
import { toast } from 'react-toastify';

const MAX_FILE_SIZE_MB = 25;

interface ReportUploadManagerProps {
    networkId: number;
}

interface Report {
    reportId: string;
    fileName: string;
    originalName: string;
    size: number;
    uploadedAt: number;
    uploadedBy: string;
    expiresAt: number;
    downloadCount: number;
    deletedAt?: number | null;
}

type OrderDirection = 'asc' | 'desc';
type OrderByField = keyof Report;

export const ReportUploadManager: React.FC<ReportUploadManagerProps> = ({ networkId }) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<{ id: string; name: string } | null>(null);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [orderBy, setOrderBy] = useState<OrderByField>('uploadedAt');
    const [order, setOrder] = useState<OrderDirection>('desc');

    const { data: reports = [], isLoading: isLoadingReports, refetch } = useGetReports(networkId);
    const { mutate: uploadReport, isLoading: isUploading } = useUploadReport({
        onSuccess: () => {
            toast.success('Report uploaded successfully');
            refetch();
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to upload report');
        },
    });

    const { mutate: deleteReport, isLoading: isDeleting } = useDeleteReport({
        onSuccess: () => {
            toast.success('Report deleted successfully');
            refetch();
            setDeleteDialogOpen(false);
            setReportToDelete(null);
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to delete report');
            setDeleteDialogOpen(false);
            setReportToDelete(null);
        },
    });

    const { mutate: downloadReport } = useDownloadReport({
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to download report');
        },
    });

    const { mutate: revertReportDeletion, isLoading: isReverting } = useRevertReportDeletion({
        onSuccess: () => {
            toast.success('Report deletion reverted successfully');
            refetch();
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to revert deletion');
        },
    });

    const checkAndUploadFile = useCallback(
        (file: File) => {
            // Check if a file with the same name already exists
            const existingReport = reports.find((report: Report) => report.originalName === file.name);

            if (existingReport) {
                // File with same name exists, show confirmation dialog
                setPendingFile(file);
                setDuplicateDialogOpen(true);
            } else {
                // No duplicate, proceed with upload
                uploadReport({ networkId, file });
            }
        },
        [networkId, uploadReport, reports]
    );

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length === 0) {
                toast.error('Please select a valid PDF file');
                return;
            }

            const file = acceptedFiles[0];

            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                toast.error(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
                return;
            }

            checkAndUploadFile(file);
        },
        [checkAndUploadFile]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
        },
        multiple: false,
        disabled: isUploading,
    });

    const handleDeleteClick = (reportId: string, reportName: string) => {
        setReportToDelete({ id: reportId, name: reportName });
        setDeleteDialogOpen(true);
    };

    const handleDownloadClick = (reportId: string, fileName: string) => {
        downloadReport({ networkId, reportId, fileName });
    };

    const handleRevertClick = (reportId: string) => {
        revertReportDeletion({ networkId, reportId });
    };

    const handleDeleteConfirm = () => {
        if (reportToDelete) {
            deleteReport({ networkId, reportId: reportToDelete.id });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setReportToDelete(null);
    };

    const handleDuplicateConfirm = () => {
        if (pendingFile) {
            uploadReport({ networkId, file: pendingFile });
        }
        setDuplicateDialogOpen(false);
        setPendingFile(null);
    };

    const handleDuplicateCancel = () => {
        setDuplicateDialogOpen(false);
        setPendingFile(null);
    };

    const formatDate = (date: number) => {
        return new Date(date * 1000).toLocaleString();
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const handleRequestSort = (property: OrderByField) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredAndSortedReports = useMemo(() => {
        let filtered = reports.filter((report: Report) => {
            const searchLower = searchQuery.toLowerCase();
            return report.originalName.toLowerCase().includes(searchLower) || report.uploadedBy.toLowerCase().includes(searchLower);
        });

        return filtered.sort((a: Report, b: Report) => {
            let aValue: any = a[orderBy];
            let bValue: any = b[orderBy];

            // Handle null/undefined - put them at the end
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Handle date comparisons
            if (aValue instanceof Date) aValue = aValue.getTime();
            if (bValue instanceof Date) bValue = bValue.getTime();

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [reports, searchQuery, orderBy, order]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Uploaded Reports
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
                Upload PDF reports that will be available to customers in the Paradome Console Data Exports tab.
            </Typography>

            <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper }}>
                <CardContent sx={{ backgroundColor: theme.palette.background.paper }}>
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'grey.400',
                            borderRadius: 1,
                            p: 4,
                            textAlign: 'center',
                            cursor: isUploading ? 'not-allowed' : 'pointer',
                            backgroundColor: theme.palette.background.paper,
                            color: '#fff',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: isUploading ? 'grey.400' : 'primary.main',
                                backgroundColor: theme.palette.background.paper,
                            },
                        }}
                    >
                        <input {...getInputProps()} />
                        {isUploading ? (
                            <CircularProgress sx={{ color: '#fff' }} />
                        ) : (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <CloudUploadIcon style={{ fontSize: 48, color: '#fff' }} />
                                </Box>
                                <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                                    {isDragActive ? 'Drop the PDF file here' : 'Drag & drop a PDF file here'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#fff' }}>
                                    or click to select a file
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#fff', mt: 1, display: 'block' }}>
                                    {`Maximum file size: ${MAX_FILE_SIZE_MB}MB`}
                                </Typography>
                            </>
                        )}
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ backgroundColor: theme.palette.background.paper }}>
                <CardContent sx={{ backgroundColor: theme.palette.background.paper }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#fff' }}>
                            Uploaded Files
                        </Typography>
                        <TextField
                            placeholder="Search reports..."
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ color: '#fff' }} />
                                    </InputAdornment>
                                ),
                                style: { color: '#fff' },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.4)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#fff',
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                    {isLoadingReports ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress sx={{ color: '#fff' }} />
                        </Box>
                    ) : filteredAndSortedReports.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#fff', p: 2, textAlign: 'center' }}>
                            {reports.length === 0 ? 'No reports uploaded yet' : 'No reports match your search'}
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
                            <Table sx={{ backgroundColor: theme.palette.background.paper, color: '#fff' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}>
                                            <TableSortLabel
                                                active={orderBy === 'originalName'}
                                                direction={orderBy === 'originalName' ? order : 'asc'}
                                                onClick={() => handleRequestSort('originalName')}
                                                sx={{
                                                    color: '#fff !important',
                                                    '&:hover': { color: '#fff !important' },
                                                    '&.Mui-active': { color: '#fff !important' },
                                                    '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                }}
                                            >
                                                File Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}>
                                            <TableSortLabel
                                                active={orderBy === 'size'}
                                                direction={orderBy === 'size' ? order : 'asc'}
                                                onClick={() => handleRequestSort('size')}
                                                sx={{
                                                    color: '#fff !important',
                                                    '&:hover': { color: '#fff !important' },
                                                    '&.Mui-active': { color: '#fff !important' },
                                                    '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                }}
                                            >
                                                Size
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}>
                                            <TableSortLabel
                                                active={orderBy === 'uploadedBy'}
                                                direction={orderBy === 'uploadedBy' ? order : 'asc'}
                                                onClick={() => handleRequestSort('uploadedBy')}
                                                sx={{
                                                    color: '#fff !important',
                                                    '&:hover': { color: '#fff !important' },
                                                    '&.Mui-active': { color: '#fff !important' },
                                                    '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                }}
                                            >
                                                Uploaded By
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}>
                                            <TableSortLabel
                                                active={orderBy === 'uploadedAt'}
                                                direction={orderBy === 'uploadedAt' ? order : 'asc'}
                                                onClick={() => handleRequestSort('uploadedAt')}
                                                sx={{
                                                    color: '#fff !important',
                                                    '&:hover': { color: '#fff !important' },
                                                    '&.Mui-active': { color: '#fff !important' },
                                                    '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                }}
                                            >
                                                Upload Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}>
                                            <TableSortLabel
                                                active={orderBy === 'expiresAt'}
                                                direction={orderBy === 'expiresAt' ? order : 'asc'}
                                                onClick={() => handleRequestSort('expiresAt')}
                                                sx={{
                                                    color: '#fff !important',
                                                    '&:hover': { color: '#fff !important' },
                                                    '&.Mui-active': { color: '#fff !important' },
                                                    '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                }}
                                            >
                                                Expires On
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}>
                                            <TableSortLabel
                                                active={orderBy === 'downloadCount'}
                                                direction={orderBy === 'downloadCount' ? order : 'asc'}
                                                onClick={() => handleRequestSort('downloadCount')}
                                                sx={{
                                                    color: '#fff !important',
                                                    '&:hover': { color: '#fff !important' },
                                                    '&.Mui-active': { color: '#fff !important' },
                                                    '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                }}
                                            >
                                                Downloads
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}>
                                            <TableSortLabel
                                                active={orderBy === 'deletedAt'}
                                                direction={orderBy === 'deletedAt' ? order : 'asc'}
                                                onClick={() => handleRequestSort('deletedAt')}
                                                sx={{
                                                    color: '#fff !important',
                                                    '&:hover': { color: '#fff !important' },
                                                    '&.Mui-active': { color: '#fff !important' },
                                                    '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                }}
                                            >
                                                Deleted At
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#fff', backgroundColor: theme.palette.background.paper }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAndSortedReports.map((report: Report) => (
                                        <TableRow key={report.reportId}>
                                            <TableCell sx={{ color: '#fff' }}>{report.originalName}</TableCell>
                                            <TableCell sx={{ color: '#fff' }}>{formatFileSize(report.size)}</TableCell>
                                            <TableCell sx={{ color: '#fff' }}>{report.uploadedBy}</TableCell>
                                            <TableCell sx={{ color: '#fff' }}>{formatDate(report.uploadedAt)}</TableCell>
                                            <TableCell sx={{ color: '#fff' }}>{formatDate(report.expiresAt)}</TableCell>
                                            <TableCell sx={{ color: '#fff' }}>{report.downloadCount || 0}</TableCell>
                                            <TableCell sx={{ color: report.deletedAt ? '#ff9800' : '#fff' }}>{report.deletedAt ? formatDate(report.deletedAt) : '—'}</TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                    <IconButton
                                                        onClick={() => handleDownloadClick(report.reportId, report.originalName)}
                                                        color="primary"
                                                        size="small"
                                                        title="Download"
                                                    >
                                                        <GetAppIcon />
                                                    </IconButton>
                                                    {report.deletedAt ? (
                                                        <IconButton
                                                            onClick={() => handleRevertClick(report.reportId)}
                                                            color="success"
                                                            size="small"
                                                            title="Revert Deletion"
                                                            disabled={isReverting}
                                                        >
                                                            <RestoreIcon />
                                                        </IconButton>
                                                    ) : null}
                                                    <IconButton onClick={() => handleDeleteClick(report.reportId, report.originalName)} color="error" size="small" title="Delete">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog
                isOpen={deleteDialogOpen}
                onClose={handleDeleteCancel}
                title="Delete Report"
                body={`Are you sure you want to delete "${reportToDelete?.name}"? This action cannot be undone.`}
                onSubmit={handleDeleteConfirm}
                onDismiss={handleDeleteCancel}
                submitText="Delete"
                dismissText="Cancel"
                isLoading={isDeleting}
                Component={undefined}
                props={undefined}
            />

            <Dialog
                isOpen={duplicateDialogOpen}
                onClose={handleDuplicateCancel}
                title="Duplicate File Name"
                body={`A file named "${pendingFile?.name}" already exists. The new file will be uploaded with a sanitized name and both versions will be available.`}
                onSubmit={handleDuplicateConfirm}
                onDismiss={handleDuplicateCancel}
                submitText="Upload Anyway"
                dismissText="Cancel"
                isLoading={isUploading}
                Component={undefined}
                props={undefined}
            />
        </Box>
    );
};
